import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';

export interface RecipientInfo {
  id: string;
  organizationName: string;
}

export interface MessageItem {
  id: number;
  title: string;
  content: string;
  senderName: string;
  isRead: boolean;
  isBroadcast: boolean;
  recipients?: RecipientInfo[];
  createdAt: string;
}

class MessageStore {
  messages: MessageItem[] = [];
  totalCount: number = 0;
  unreadCount: number = 0;
  isLoading: boolean = false;
  pollingInterval: NodeJS.Timeout | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchUnreadCount() {
    try {
      const response = await axios.get('/api/v1/messages/unread-count');
      runInAction(() => {
        this.unreadCount = response.data.unreadCount;
      });
    } catch (error) {
      // 인증 안 된 상태에서는 무시
    }
  }

  async fetchMessages(page: number = 0) {
    this.isLoading = true;
    try {
      const response = await axios.get(`/api/v1/messages?page=${page}&rowsPerPage=20`);
      runInAction(() => {
        this.messages = response.data.messages;
        this.totalCount = response.data.totalCount;
        this.unreadCount = response.data.unreadCount;
      });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async markAsRead(messageId: number) {
    try {
      await axios.put(`/api/v1/messages/${messageId}/read`);
      runInAction(() => {
        const msg = this.messages.find(m => m.id === messageId);
        if (msg && !msg.isRead) {
          msg.isRead = true;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
      });
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  }

  startPolling() {
    this.fetchUnreadCount();
    this.pollingInterval = setInterval(() => {
      this.fetchUnreadCount();
    }, 30000);
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  reset() {
    this.messages = [];
    this.totalCount = 0;
    this.unreadCount = 0;
    this.stopPolling();
  }
}

export const messageStore = new MessageStore();
